import { useState } from "react";
import { format } from "date-fns";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import clsx from "clsx";
import { Calendar, ChevronLeft, ChevronRight, CircleX } from "lucide-react";
import { cn, MONTHS } from "@/lib/utils";

const MonthRangePicker = ({
  value,
  onChange,
  placeholder,
  onClear,
  disabled,
  maxMonthYear,
  minMonthYear,
}: {
  value: { from: Date | null; to: Date | null };
  onChange: (range: { from: Date | null; to: Date | null }) => void;
  placeholder?: string;
  onClear: () => void;
  disabled?: boolean;
  maxMonthYear?: string; // "MM/YYYY"
  minMonthYear?: string; // "MM/YYYY"
}) => {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  // Parse Min Date Limit ("MM/YYYY")
  const minMonth = minMonthYear ? Number(minMonthYear.split("/")[0]) : null;
  const minYear = minMonthYear ? Number(minMonthYear.split("/")[1]) : null;

  // Parse Max Date Limit ("MM/YYYY")
  const maxMonth = maxMonthYear ? Number(maxMonthYear.split("/")[0]) : null;
  const maxYear = maxMonthYear ? Number(maxMonthYear.split("/")[1]) : null;

  // Check if a specific month button should be disabled
  const isMonthDisabled = (monthIndex: number) => {
    // Check against minimum limit
    if (minYear && minMonth) {
      if (year < minYear) return true;
      if (year === minYear && monthIndex + 1 < minMonth) return true;
    }

    // Check against maximum limit
    if (maxYear && maxMonth) {
      if (year > maxYear) return true;
      if (year === maxYear && monthIndex + 1 > maxMonth) return true;
    }

    return false;
  };

  const handleSelect = (monthIndex: number) => {
    if (isMonthDisabled(monthIndex)) return;
    const selectedDate = new Date(year, monthIndex, 1);

    if (!value?.from || (value?.from && value?.to)) {
      onChange({ from: selectedDate, to: null });
    } else {
      const from = value?.from;
      const to = selectedDate;
      if (from > to) {
        onChange({ from: to, to: null });
      } else {
        onChange({ from, to });
        setOpen(false);
      }
    }
  };

  const isInRange = (index: number) => {
    if (!value?.from || !value?.to) return false;
    const fromIndex = value?.from?.getMonth();
    const toIndex = value?.to?.getMonth();
    return (
      index >= fromIndex &&
      index <= toIndex &&
      value?.from?.getFullYear() === year
    );
  };

  const isSelected = (index: number) => {
    return (
      (value?.from &&
        value?.from?.getFullYear() === year &&
        value?.from?.getMonth() === index) ||
      (value?.to &&
        value?.to?.getFullYear() === year &&
        value?.to?.getMonth() === index)
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-between text-left font-normal  h-[40px]",
            value?.from && value?.to
              ? "text-black hover:text-black"
              : "text-g-400 hover:text-g-400"
          )}
        >
          {value?.from && value?.to
            ? `${format(value.from, "MMM yyyy")} - ${format(
                value.to,
                "MMM yyyy"
              )}`
            : placeholder ?? "Select month range"}
          {disabled ? (
            <></>
          ) : value?.from && value?.to ? (
            <div
              className="h-5 w-5 cursor-pointer flex items-center justify-end"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation(); // Prevents triggering the button click
                onClear && onClear();
              }}
            >
              <CircleX className="stroke-gray-500 group-hover:stroke-black" />
            </div>
          ) : (
            <Calendar className="text-g-300 ml-3" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-4">
        <div className="flex justify-between items-center mb-3">
          <button
            onClick={() => setYear((y) => y - 1)}
            className="px-2 py-1 text-gray-500 hover:text-black"
            disabled={!!(minYear && year > minYear)}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="font-semibold">{year}</span>
          <button
            onClick={() => setYear((y) => y + 1)}
            className="px-2 py-1 text-gray-500 hover:text-black"
            disabled={!!(maxYear && year < maxYear)}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {MONTHS.map((month, idx) => {
            const disabledMonth = isMonthDisabled(idx);
            return (
              <button
                key={month}
                onClick={() => handleSelect(idx)}
                className={clsx(
                  "p-3 rounded text-sm",
                  disabledMonth
                    ? "opacity-30 cursor-not-allowed bg-gray-50 text-gray-400" :
                    isSelected(idx)
                      ? "bg-primary text-white"
                      : isInRange(idx)
                        ? "bg-blue-100 text-blue-700"
                        : "hover:bg-muted"
                )}
              >
                {month}
              </button>
            )}
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MonthRangePicker;
